import type { CycleAnalytics, CycleTemplatesListResult } from './cycles/models/CycleTypes';
import type { ScoreAnalytics, ScoreSeriesEntry } from './score/models/ScoreTypes';

export type LookupFilters = Record<string, string | null | undefined>;

export enum AnalyticsStatus {
	Idle = 'idle',
	Opening = 'opening',
	Loading = 'loading',
	Ingesting = 'ingesting',
	NotExists = 'not-exists',
	Succeeded = 'succeeded',
	Failed = 'failed',
}

export interface ManagerEdge {
	managerID: string;
	// Null for MANAGER edges that carry no role; such edges are still part of the
	// unfiltered manager set, they just never match a specific role.
	roleID: string | null;
}

export interface Entity {
	_department: string | null;
	_department_path: string[];
	_direct_managers: ManagerEdge[];
	_managers: string[];
	_teams: string[];
	_employment_ended: Date | null;
	// Historical patches (newest first): each entry holds the state as it was
	// *before* `_date`. Only present on Users records.
	_rollback?: RollbackEntry[];
	[key: string]: unknown;
}

export interface RollbackEntry {
	_date: string;
	_department?: string | null;
	_department_path?: string[] | null;
	_teams?: string[];
	_direct_managers?: ManagerEdge[];
	_indirect_managers?: ManagerEdge[];
	[key: string]: unknown;
}

export interface AnalyticsFilters {
	endDate: Date;
	startDate: Date;
	synced: string | null;
	timeInterval: string | null;
	userFilter: 'active' | 'all';
	engagementTemplateID?: string;
	engagementRequestID?: string;
}

export type WorkerRequest =
	| { type: 'abort' }
	| { type: 'init'; name: string }
	| { type: 'load'; workspace: string; user?: string; generate?: boolean }
	| { type: 'record'; id: string; store: string; key: string }
	| {
			type: 'cycles';
			id: string;
			cycleTemplateID: string;
			startDate: Date;
			endDate: Date;
			groupIDs?: string[];
			roleID?: string;
	  }
	| { type: 'cycleTemplates'; id: string }
	| { type: 'score'; id: string; endDate: Date; seriesEndDates?: Date[] }
	| { type: 'query'; id: string; source: string; pipeline: Record<string, unknown>[] };

export type WorkerResponse =
	| { type: 'ready'; empty: boolean }
	| { type: 'error'; message: string }
	| { type: 'status'; status: AnalyticsStatus }
	| { type: 'synced'; syncedAt: string }
	| { type: 'record'; id: string; data?: Record<string, unknown> | null }
	| { type: 'cycles'; id: string; data: CycleAnalytics }
	| { type: 'cycleTemplates'; id: string; data: CycleTemplatesListResult }
	| { type: 'score'; id: string; data: ScoreAnalytics; series?: ScoreSeriesEntry[] }
	| { type: 'query'; id: string; rows?: Record<string, unknown>[]; error?: string };

export interface User extends Entity {
	userID: string;
	firstName?: string;
	lastName?: string;
	groupID?: string | null;
	activatedAt?: Date | null;
	deactivatedAt?: Date | null;
	archivedAt?: Date | null;
	isArchived?: boolean;
	excludeAnalytics?: boolean;
}

export interface Group {
	groupID: string;
	name?: unknown;
	type?: string;
	parentID?: string;
}
